/**
 * Navigation script for primary navigation menu
 * Handles menu interactions and dropdown behavior
 */

import { BREAKPOINTS } from '../variables.ts';

((Drupal, once) => {
  'use strict';

  /**
   * Drupal behavior for sticky header with hide-on-scroll-down / show-on-scroll-up.
   *
   * @description
   * The header is always sticky (position: sticky via CSS). While the user
   * scrolls DOWN the header slides out of view. As soon as the user scrolls UP
   * it slides back in. The header is never hidden when the page is near the top
   * (threshold: 80 px).
   */
  Drupal.behaviors.tpStickyHeader = {
    attach(context: Document | HTMLElement) {
      const headers = once('tp-sticky-header', '.tp-header', context);

      if (headers.length <= 0) {
        return;
      }

      headers.forEach((el) => {
        const header = el as HTMLElement;
        let lastScrollY = window.scrollY;
        let ticking = false;

        // Publish the header's rendered height so sticky elements (CTA, ToC)
        // can offset themselves correctly when the header resizes across
        // breakpoints. Kept in sync via a ResizeObserver.
        const publishHeaderHeight = () => {
          document.documentElement.style.setProperty('--tp-header-height', `${header.offsetHeight}px`);
        };
        publishHeaderHeight();
        new ResizeObserver(publishHeaderHeight).observe(header);

        // Read Drupal toolbar offset so the threshold accounts for the admin bar.
        const getDrupalOffset = () =>
          parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--drupal-displace-offset-top') || '0',
            10
          ) || 0;

        const SCROLL_THRESHOLD = 80; // px of page scroll before hide/show kicks in

        const isMobile = () => window.innerWidth <= BREAKPOINTS.lg;

        const update = () => {
          if (!isMobile()) {
            // On desktop reset any state classes and let CSS sticky do the work.
            document.body.classList.remove('tp-header--hidden');
            ticking = false;
            return;
          }

          // Never hide the header while the mobile menu is open.
          if (document.body.classList.contains('mobile-menu-open')) {
            ticking = false;
            return;
          }

          const currentScrollY = window.scrollY;
          const scrollingDown = currentScrollY > lastScrollY;
          const threshold = SCROLL_THRESHOLD + getDrupalOffset();

          if (currentScrollY <= threshold) {
            // Always visible near the top of the page
            document.body.classList.remove('tp-header--hidden');
          } else if (scrollingDown) {
            // Scrolling down — hide the header
            document.body.classList.add('tp-header--hidden');
            // Also close mobile menu when header hides
            document.body.classList.remove('mobile-menu-open');
            const toggleBtn = header.querySelector<HTMLButtonElement>('.tp-header__mobile-toggle');
            if (toggleBtn) {
              toggleBtn.setAttribute('aria-expanded', 'false');
            }
          } else {
            // Scrolling up — show the header
            document.body.classList.remove('tp-header--hidden');
          }

          lastScrollY = currentScrollY;
          ticking = false;
        };

        window.addEventListener(
          'scroll',
          () => {
            if (!ticking) {
              window.requestAnimationFrame(update);
              ticking = true;
            }
          },
          { passive: true }
        );

        // When resizing from mobile → desktop, clean up any leftover hidden state.
        window.addEventListener(
          'resize',
          () => {
            if (!isMobile()) {
              document.body.classList.remove('tp-header--hidden');
            }
          },
          { passive: true }
        );
      });
    },
  };

  /**
   * Drupal behavior for mobile menu toggle
   */
  Drupal.behaviors.tpMobileMenuToggle = {
    attach(context: Document | HTMLElement) {
      const toggleButtons = once('tp-mobile-toggle', '.tp-header__mobile-toggle', context);

      toggleButtons.forEach((button) => {
        const toggleBtn = button as HTMLButtonElement;

        toggleBtn.addEventListener('click', () => {
          const header = toggleBtn.closest('.tp-header');
          if (!header) {
            return;
          }

          const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
          const newState = !isExpanded;

          // Toggle aria-expanded on button
          toggleBtn.setAttribute('aria-expanded', newState ? 'true' : 'false');

          // Toggle class on body
          if (newState) {
            document.body.classList.add('mobile-menu-open');
            document.documentElement.classList.add('no-scroll');
          } else {
            document.body.classList.remove('mobile-menu-open');
            document.documentElement.classList.remove('no-scroll');
          }
        });
      });
    },
  };

  /**
   * Drupal behavior for primary navigation menu
   */
  Drupal.behaviors.tpNavigation = {
    attach(context: Document | HTMLElement) {
      // Find all navigation menus using once to prevent duplicate initialization
      const menus = once(
        'tp-navigation',
        '.tp-navigation-primary-menu .menu, .tp-navigation-secondary-menu .menu, .tp-navigation-country-menu .menu',
        context
      );

      menus.forEach((menu) => {
        // Check if this is the primary navigation menu
        const isPrimaryMenu = menu.closest('.tp-navigation-primary-menu');

        // If it's the primary menu, check the last direct child
        if (isPrimaryMenu) {
          const directChildren = menu.querySelectorAll('.menu > .menu__item');
          if (directChildren.length > 0) {
            const lastItem = directChildren[directChildren.length - 1] as HTMLElement;
            // Check if the last item contains a nested item with children
            const nestedItemWithChildren = lastItem.querySelector('.submenu__item--has-children');
            if (nestedItemWithChildren) {
              // Add a special class to indicate this submenu should slide from left
              nestedItemWithChildren.classList.add('submenu--slide-left');
            }
          }
        }

        // Find all menu items with children (including nested submenu items)
        const itemsWithChildren = menu.querySelectorAll('.menu__item--has-children, .submenu__item--has-children');

        const collapseItem = (item: HTMLElement) => {
          const btn = item.querySelector<HTMLButtonElement>(':scope > .menu__button');
          const sub = item.querySelector<HTMLElement>(':scope > .submenu');
          if (btn && sub) {
            item.setAttribute('data-expanded', 'false');
            btn.setAttribute('aria-expanded', 'false');
            sub.setAttribute('aria-hidden', 'true');
          }
        };

        const closeSiblings = (current: HTMLElement) => {
          const parentList = current.parentElement;
          if (!parentList) {
            return;
          }
          const siblings = parentList.querySelectorAll(
            ':scope > .menu__item--has-children, :scope > .submenu__item--has-children'
          );
          siblings.forEach((sibling) => {
            if (sibling !== current) {
              collapseItem(sibling as HTMLElement);
            }
          });
        };

        itemsWithChildren.forEach((item) => {
          const menuItem = item as HTMLElement;
          const button = menuItem.querySelector<HTMLButtonElement>(':scope > .menu__button');
          const submenu = menuItem.querySelector<HTMLElement>(':scope > .submenu');

          if (!button || !submenu) {
            return;
          }

          const setExpanded = (expanded: boolean, closeOthers = false) => {
            if (expanded && closeOthers) {
              closeSiblings(menuItem);
            }
            menuItem.setAttribute('data-expanded', expanded ? 'true' : 'false');
            button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
            submenu.setAttribute('aria-hidden', expanded ? 'false' : 'true');
          };

          // Ensure initial state
          setExpanded(menuItem.getAttribute('data-expanded') === 'true');

          // Toggle on button click
          button.addEventListener('click', () => {
            const isExpanded = menuItem.getAttribute('data-expanded') === 'true';
            setExpanded(!isExpanded, !isExpanded);
          });

          // Open/close on hover (mouse users) - only on desktop
          const isMobile = () => window.innerWidth <= BREAKPOINTS.lg;

          menuItem.addEventListener('mouseenter', () => {
            if (!isMobile()) {
              setExpanded(true, true);
            }
          });

          menuItem.addEventListener('mouseleave', () => {
            if (!isMobile()) {
              setExpanded(false);
            }
          });

          // Keyboard accessibility - toggle on Enter/Space and close on Escape
          button.addEventListener('keydown', (event: Event) => {
            const keyEvent = event as KeyboardEvent;
            if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
              keyEvent.preventDefault();
              const isExpanded = menuItem.getAttribute('data-expanded') === 'true';
              setExpanded(!isExpanded, !isExpanded);
            }
            if (keyEvent.key === 'Escape') {
              setExpanded(false);
              button.focus();
            }
          });
        });

        // Close submenus when clicking outside
        document.addEventListener('click', (event) => {
          const target = event.target as HTMLElement;
          if (!menu.contains(target)) {
            itemsWithChildren.forEach((item) => {
              const menuItem = item as HTMLElement;
              const button = menuItem.querySelector<HTMLButtonElement>(':scope > .menu__button');
              const submenu = menuItem.querySelector<HTMLElement>(':scope > .submenu');
              if (button && submenu) {
                menuItem.setAttribute('data-expanded', 'false');
                button.setAttribute('aria-expanded', 'false');
                submenu.setAttribute('aria-hidden', 'true');
              }
            });
          }
        });
      });
    },
  };
})(Drupal, once);
